/* eslint-disable react/no-children-prop */
import React, { useState, useEffect, useRef, useContext } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { Grid, Box } from '@material-ui/core/';
import LinearProgress from '@material-ui/core/LinearProgress';

import Dialog from '../../../../UI/Dialog/Dialog';
import Button from '../../../../UI/Button/Button';
import InputField from '../../../../UI/InputField/InputField';
import ErrorMessage from '../../../../UI/ErrorMessage/ErrorMessage';
import { PassengerInfoContext } from '../../../../../context/passengerInfo';
import Text from '../../../../UI/Text/Text';

// Styles
const useStyles = makeStyles((theme) => ({
  description: {
    marginBottom: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 16px 16px 16px',
  },
  actionLeft: {
    marginRight: theme.spacing(2),
  },
  loading: {
    marginTop: theme.spacing(2),
  },
}));

const ContactDetailsModal = (props) => {
  const classes = useStyles();

  // Props
  const {
    close, buttonText, flight, open,
  } = props;

  const {
    updateContactInfo, updateContactInfoLoading, updateContactInfoError, setUpdateContactInfoError,
  } = useContext(PassengerInfoContext);

  const passenger = flight?.passengers?.[0];

  useEffect(() => () => {
    setUpdateContactInfoError();
  }, [setUpdateContactInfoError]);

  // State
  const [values, setValues] = useState({ email: '', mobile: '' });
  const [valid, setValid] = useState(false);
  const [changedValue, setChangedValue] = useState(false);

  // Refs
  const firstUpdate = useRef(true);

  // Mount
  useEffect(() => {
    if (firstUpdate.current) {
      setValues({
        email: passenger?.email ? passenger.email : '',
        mobile: passenger?.mobile ? passenger.mobile : '',
      });
      firstUpdate.current = false;
    }
  }, [passenger]);

  // Validation
  useEffect(() => {
    // Email validation
    let emailValid = false;
    if (values.email) {
      emailValid = true;
      if (passenger?.email && passenger.email.toLowerCase() !== values.email.toLowerCase()) {
        emailValid = false;
      }
    }
    // Mobile validation
    let mobileValid = false;
    if (values.mobile) {
      mobileValid = true;
      if (passenger?.mobile && passenger.mobile.toLowerCase() !== values.mobile.toLowerCase()) {
        mobileValid = false;
      }
    }
    setValid(emailValid || mobileValid);
  }, [passenger, values]);

  // Toggle modal
  const handleCancel = (close);

  // Handle change
  const handleChange = (name) => (event) => {
    if (!changedValue) {
      setChangedValue(true);
    }
    setValues({
      ...values,
      [name]: event.target.value.toUpperCase(),
    });
  };

  // Update comments
  const handleUpdateContact = () => {
    if (!changedValue) {
      close();
      return;
    }
    const data = {
      passenger: {
        surName: passenger.name.surName,
        firstName: passenger.name.firstName,
        passengerIdentifier: passenger.passengerIdentifier,
        type: passenger.type,
      },
      pnr: passenger.pnr,
      mobile: values.mobile,
      email: values.email,
      station: flight?.station?.from?.code,
      carrier: flight?.carrier,
      primeFlight: {
        carrier: flight?.carrier,
        flightNr: flight?.flightNr,
        scheduledDeparture: flight?.flightTime?.scheduledDeparture,
        departureStation: flight?.station?.from?.code,
        boardPoint: flight?.station?.from?.code,
      },
    };

    updateContactInfo(data).then((res) => {
      if (res && !res.error) {
        close();
      }
    });
  };

  let progressBar;
  if (updateContactInfoLoading) {
    progressBar = <LinearProgress color="secondary" />;
  }

  let errorMessage;
  if (updateContactInfoError) {
    errorMessage = <ErrorMessage error="Error updating booking info" />;
  }

  // Modal Content
  const modalContent = (
    <>
      <InputField
        value={values.email}
        onChange={handleChange('email')}
        label="Email"
        id="email"
        fullWidth
      />
      <Box mt={2} />
      <InputField
        value={values.mobile}
        onChange={handleChange('mobile')}
        label="Mobile"
        id="mobile"
        fullWidth
      />
      {errorMessage}
      {progressBar}
    </>
  );

  // Modal Actions
  const modalActions = (
    <Grid container className={classes.actions}>
      <Grid item className={classes.actionLeft}>
        <Button
          text="Cancel"
          color="secondary"
          action={handleCancel}
        />
      </Grid>
      <Grid item>
        <Button
          text="Update"
          color="primary"
          action={handleUpdateContact}
          disabled={!valid}
        />
      </Grid>
    </Grid>
  );

  return (
    <Dialog
      buttonText={buttonText || null}
      buttonColor="secondary"
      title={<Text id="summary-card-action-contact-title" />}
      open={open}
      setOpen={close}
      children={modalContent}
      action={modalActions}
    />
  );
};

export default ContactDetailsModal;
